import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { compactArray } from '@mews/fp';
import { Body, Tag } from '@optimus-web/core';
import { UserProfileResult } from '@mews-commander/data-access';
import { LocalizedDateFromUtc } from '@mews-commander/ui';
import { UserTableActions } from '../../UserTableActions';
import { UserTableAvatar } from '../../UserTableAvatar';
import { useUserContext } from '../../../context';
import { UserType } from '../../../types';
import { useDisplaySourceColumn } from '../../../hooks/useDisplaySourceColumn';
import { UserStatusTag } from '../../UserStatusTag';
import { SecurityStatus } from '../../UserDetail/UserDetailContent/SecurityStatus';

const columnHelper = createColumnHelper<UserProfileResult>();

export const useColumns = () => {
    const { userType } = useUserContext();
    const isPropertyUser = userType === UserType.Local;
    const displaySourceColumn = useDisplaySourceColumn();

    return compactArray([
        columnHelper.accessor('fullName', {
            id: 'name',
            header: () => <LocalizedText textKey={TextKey.Name} />,
            cell: (props) => <UserTableAvatar preventClick={!isPropertyUser} userProfile={props.row.original} />,
            enableSorting: true,
            meta: {
                width: 300,
            },
        }),
        columnHelper.accessor('superiorName', {
            header: () => <LocalizedText textKey={TextKey.Supervisor} />,
            cell: (props) => {
                const value = props.getValue();
                return value ? (
                    <Body size="small" ellipsis maxLines={1}>
                        {value}
                    </Body>
                ) : (
                    UnicodeEntity.Dash
                );
            },
            enableSorting: true,
        }),
        isPropertyUser
            ? columnHelper.accessor('departments', {
                  id: 'department',
                  header: () => <LocalizedText textKey={TextKey.Department} />,
                  cell: (props) => {
                      const value = props.getValue();
                      return value ? (
                          <Body size="small" ellipsis maxLines={1}>
                              {value}
                          </Body>
                      ) : (
                          UnicodeEntity.Dash
                      );
                  },
                  enableSorting: true,
              })
            : null,
        columnHelper.accessor('roles', {
            header: () => <LocalizedText textKey={TextKey.Roles} />,
            cell: (props) => {
                const value = props.getValue();
                return value ? (
                    <Body size="small" ellipsis maxLines={1}>
                        {value}
                    </Body>
                ) : (
                    UnicodeEntity.Dash
                );
            },
            enableSorting: true,
        }),
        columnHelper.accessor('lastLogIn', {
            header: () => <LocalizedText textKey={TextKey.LastLogIn} />,
            cell: (props) => {
                const value = props.getValue();
                return value == null ? UnicodeEntity.Dash : <LocalizedDateFromUtc utcString={value} />;
            },
            enableSorting: true,
        }),
        columnHelper.accessor('status', {
            header: () => <LocalizedText textKey={TextKey.Status} />,
            cell: (props) => {
                const status = props.getValue();
                return <UserStatusTag status={status} />;
            },
            enableSorting: true,
        }),
        displaySourceColumn
            ? columnHelper.accessor('isScimProvisioned', {
                  header: () => <LocalizedText textKey={TextKey.Source} />,
                  cell: (props) => {
                      const source = props.getValue();
                      return <Tag variant="basicSubtle">{source ? lt(TextKey.IDP) : lt(TextKey.Mews)}</Tag>;
                  },
                  enableSorting: true,
              })
            : null,
        columnHelper.accessor('securityFeatures', {
            header: () => <LocalizedText textKey={TextKey.Authentication} />,
            cell: ({ getValue }) => <SecurityStatus securityFeatures={getValue()} />,
        }),
        isPropertyUser
            ? columnHelper.display({
                  id: 'actions',
                  header: '',
                  meta: { width: 100, align: 'end' },
                  cell: (props) => <UserTableActions userProfile={props.row.original} />,
              })
            : null,
    ]);
};
